/** Hand-drawn SVG/CSS art for the instant-game cards — each game gets its own
 *  miniature "key art" instead of a generic icon. Pure vectors + the real LP
 *  PRMD coin render, so they stay crisp at any density. */

export function CoinFlipArt() {
  return (
    <span className="gart gart--coin">
      <span className="gart__halo" />
      <img
        src="/games/coinflip/coin-front.webp"
        alt=""
        className="gart__coin"
        draggable={false}
      />
      <span className="gart__spark gart__spark--1" />
      <span className="gart__spark gart__spark--2" />
    </span>
  );
}

export function DiceArt() {
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg" aria-hidden="true">
        <defs>
          <linearGradient id="diceTop" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fff3cf" />
            <stop offset="1" stopColor="#e9c46a" />
          </linearGradient>
          <linearGradient id="diceLeft" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#c79a3c" />
            <stop offset="1" stopColor="#8a6a1f" />
          </linearGradient>
          <linearGradient id="diceRight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#a87f2c" />
            <stop offset="1" stopColor="#6b511a" />
          </linearGradient>
        </defs>
        {/* isometric die */}
        <polygon points="32,6 54,17 32,28 10,17" fill="url(#diceTop)" />
        <polygon points="10,17 32,28 32,54 10,43" fill="url(#diceLeft)" />
        <polygon points="54,17 32,28 32,54 54,43" fill="url(#diceRight)" />
        {/* pips */}
        <ellipse cx="32" cy="17" rx="3.4" ry="2" fill="#3a2c08" />
        <ellipse cx="21" cy="30.5" rx="2.4" ry="2.8" fill="#2a1f06" />
        <ellipse cx="21" cy="40.5" rx="2.4" ry="2.8" fill="#2a1f06" />
        <ellipse cx="37" cy="30.5" rx="2.2" ry="2.6" fill="#241a05" />
        <ellipse cx="43" cy="35.5" rx="2.2" ry="2.6" fill="#241a05" />
        <ellipse cx="49" cy="40.5" rx="2.2" ry="2.6" fill="#241a05" />
      </svg>
    </span>
  );
}

export function RocketArt() {
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg" aria-hidden="true">
        <defs>
          <linearGradient id="rkBody" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#7d8598" />
            <stop offset="0.3" stopColor="#eef1f8" />
            <stop offset="0.6" stopColor="#c9cfdd" />
            <stop offset="1" stopColor="#5b6275" />
          </linearGradient>
          <linearGradient id="rkNose" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#b98a2e" />
            <stop offset="0.45" stopColor="#ffe6ac" />
            <stop offset="1" stopColor="#8a651c" />
          </linearGradient>
          <linearGradient id="rkFlame" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff3cf" />
            <stop offset="0.5" stopColor="#ffb347" />
            <stop offset="1" stopColor="#ff5722" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* the whole ship sways left/right as if steering around something */}
        <g className="gart__rocketfly">
          {/* flame — pulses stronger/weaker */}
          <g className="gart__rkflame" style={{ transformOrigin: '32px 44px' }}>
            <path d="M27 44 Q32 62 37 44 Z" fill="url(#rkFlame)" />
            <path d="M29.4 44 Q32 54 34.6 44 Z" fill="#fff3cf" opacity="0.85" />
          </g>
          {/* fins */}
          <path d="M24 34 Q16 40 18 50 L24 44 Z" fill="#c79a3c" />
          <path d="M40 34 Q48 40 46 50 L40 44 Z" fill="#8a6a1f" />
          {/* body */}
          <path d="M32 4 Q41 16 40 32 L40 44 L24 44 L24 32 Q23 16 32 4 Z" fill="url(#rkBody)" />
          {/* nose cone */}
          <path d="M32 4 Q38 10 39 18 L25 18 Q26 10 32 4 Z" fill="url(#rkNose)" />
          {/* gold band */}
          <rect x="24" y="38" width="16" height="3" fill="#e9c46a" />
          {/* porthole */}
          <circle cx="32" cy="27" r="4.6" fill="#0a1626" stroke="#f0c05a" strokeWidth="1.6" />
          <circle cx="30.5" cy="25.5" r="1.6" fill="#8cbeff" opacity="0.75" />
        </g>
        {/* exhaust sparks trailing below */}
        <circle className="gart__rkspark gart__rkspark--1" cx="30" cy="52" r="1.4" fill="#ffb347" />
        <circle className="gart__rkspark gart__rkspark--2" cx="34" cy="55" r="1.1" fill="#ffe9a8" />
      </svg>
    </span>
  );
}

export function WheelArt() {
  // 13 sectors like the real game: alternating gold-filled and transparent
  // slices divided by gold spokes, with the white-gold MAX WIN sector on top
  const segs = 13;
  const r = 26;
  const cx = 32;
  const cy = 34;
  const paths = Array.from({ length: segs }, (_, i) => {
    const a0 = (i / segs) * Math.PI * 2 - Math.PI / 2;
    const a1 = ((i + 1) / segs) * Math.PI * 2 - Math.PI / 2;
    return (
      <path
        key={i}
        d={`M${cx},${cy} L${cx + r * Math.cos(a0)},${cy + r * Math.sin(a0)} A${r},${r} 0 0 1 ${cx + r * Math.cos(a1)},${cy + r * Math.sin(a1)} Z`}
        fill={i === 0 ? '#f7e7b0' : i % 2 === 1 ? '#e9c46a' : 'none'}
        fillOpacity={i === 0 ? 0.9 : i % 2 === 1 ? 0.55 : undefined}
        stroke="#e9c46a"
        strokeWidth="1"
      />
    );
  });
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg gart__svg--wheel" aria-hidden="true">
        <g className="gart__wheelspin" style={{ transformOrigin: '32px 34px' }}>{paths}</g>
        <circle cx="32" cy="34" r="26" fill="none" stroke="#e9c46a" strokeWidth="1.4" />
        <circle cx="32" cy="34" r="7" fill="#0e0e12" stroke="#e9c46a" strokeWidth="1.6" />
        <circle cx="32" cy="34" r="2.4" fill="#e9c46a" />
        <polygon points="27.5,2 36.5,2 32,10" fill="#f7e7b0" stroke="#8a6a1f" strokeWidth="0.8" />
      </svg>
    </span>
  );
}

export function PlinkoArt() {
  const pegs: JSX.Element[] = [];
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col <= row; col++) {
      const x = 32 + (col - row / 2) * 13;
      const y = 16 + row * 11;
      pegs.push(<circle key={`${row}-${col}`} cx={x} cy={y} r="2.6" fill="#e9c46a" opacity={0.55 + row * 0.12} />);
    }
  }
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg" aria-hidden="true">
        {pegs}
        <circle className="gart__plinkoball" cx="32" cy="7" r="4.4" fill="#f7e7b0" stroke="#8a6a1f" strokeWidth="1" />
        <rect x="10" y="56" width="44" height="4" rx="2" fill="#b8922f" opacity="0.8" />
      </svg>
    </span>
  );
}

export function ScratchArt() {
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg" aria-hidden="true">
        <defs>
          <linearGradient id="scrCard" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#20202a" />
            <stop offset="1" stopColor="#101016" />
          </linearGradient>
          <linearGradient id="scrFoil" x1="0" y1="0" x2="1" y2="0.6">
            <stop offset="0" stopColor="#f7e7b0" />
            <stop offset="0.5" stopColor="#e9c46a" />
            <stop offset="1" stopColor="#b8922f" />
          </linearGradient>
        </defs>
        <rect x="8" y="12" width="48" height="40" rx="6" fill="url(#scrCard)" stroke="#e9c46a" strokeWidth="1.2" />
        <rect x="14" y="20" width="36" height="16" rx="3" fill="url(#scrFoil)" />
        {/* scratched-away notch revealing a star */}
        <path d="M40 20 h10 v16 h-16 q8 -4 6 -16 z" fill="#101016" />
        <path d="M43.5 25 l1.4 2.9 3.2 .4 -2.3 2.2 .5 3.1 -2.8 -1.5 -2.8 1.5 .5 -3.1 -2.3 -2.2 3.2 -.4 z" fill="#ffe9a8" />
        <rect x="14" y="41" width="22" height="4" rx="2" fill="#3a3a48" />
        <rect x="14" y="47" width="14" height="3" rx="1.5" fill="#2b2b36" />
      </svg>
    </span>
  );
}

export function SandArt() {
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg" aria-hidden="true">
        <defs>
          <linearGradient id="sandGlass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f7e7b0" stopOpacity="0.35" />
            <stop offset="1" stopColor="#e9c46a" stopOpacity="0.12" />
          </linearGradient>
        </defs>
        <path d="M18 8 h28 v6 q0 10 -10 16 q-4 2 -4 4 t4 4 q10 6 10 16 v6 h-28 v-6 q0 -10 10 -16 q4 -2 4 -4 t-4 -4 q-10 -6 -10 -16 z" fill="url(#sandGlass)" stroke="#e9c46a" strokeWidth="1.6" />
        <path d="M24 14 h16 q-1 8 -8 11 q-7 -3 -8 -11 z" fill="#e9c46a" />
        <path d="M26 52 h12 q-1 -7 -6 -9 q-5 2 -6 9 z" fill="#e9c46a" />
        <rect className="gart__sandstream" x="31" y="30" width="2" height="14" fill="#f7e7b0" />
        <rect x="14" y="4" width="36" height="5" rx="2.5" fill="#b8922f" />
        <rect x="14" y="55" width="36" height="5" rx="2.5" fill="#b8922f" />
      </svg>
    </span>
  );
}

export function VaultArt() {
  return (
    <span className="gart">
      <svg viewBox="0 0 64 64" className="gart__svg" aria-hidden="true">
        <defs>
          <linearGradient id="vaultBody" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#26262f" />
            <stop offset="1" stopColor="#101016" />
          </linearGradient>
          <radialGradient id="vaultDial" cx="0.4" cy="0.35" r="0.9">
            <stop offset="0" stopColor="#f7e7b0" />
            <stop offset="1" stopColor="#b8922f" />
          </radialGradient>
        </defs>
        <rect x="8" y="10" width="48" height="44" rx="8" fill="url(#vaultBody)" stroke="#e9c46a" strokeWidth="1.6" />
        <circle cx="32" cy="32" r="13" fill="none" stroke="url(#vaultDial)" strokeWidth="4" />
        <g stroke="#f7e7b0" strokeWidth="2.4" strokeLinecap="round">
          <line x1="32" y1="23" x2="32" y2="27" />
          <line x1="32" y1="37" x2="32" y2="41" />
          <line x1="23" y1="32" x2="27" y2="32" />
          <line x1="37" y1="32" x2="41" y2="32" />
        </g>
        <circle cx="32" cy="32" r="4" fill="url(#vaultDial)" />
        <rect x="49" y="24" width="3" height="16" rx="1.5" fill="#e9c46a" />
      </svg>
    </span>
  );
}
